import { Button, IconFont, Text } from '@byecode/ui'
import type {
    ActorEvent,
    ApplicationSettingThemeColor,
    BackgroundNoImageProtocol,
    RecordLikeProtocol,
    SizeConfigure
} from '@lighthouse/core'
import type { FlowLayoutNode } from '@lighthouse/shared'
import { applyFillPickerValue4CssColorValue, getBackgroundStyle, getTextColorStyle, useFillPickerContext } from '@lighthouse/shared'
import React, { memo, useCallback, useRef } from 'react'
import type { CSSObject } from 'styled-components'
import styled, { css } from 'styled-components'
import { useImmer } from 'use-immer'

import { useViewBlockContext } from '../../context'
import { Item } from '../CustomViewBlock/Item'
import { setDeadCopyNode } from '../CustomViewBlock/utils'

const ItemWrapper = styled.div`
    display: flex;
    flex-direction: column;
`

const NestList = styled.div`
    display: flex;
    flex-direction: column;
`

type ExpandButtonProps = {
    bgcolor: BackgroundNoImageProtocol | undefined
    palettes: ApplicationSettingThemeColor[]
}

const ExpandButton = styled(Button).withConfig<ExpandButtonProps>({
    shouldForwardProp: prop => prop !== 'bgcolor' && prop !== 'palettes'
})`
    align-self: flex-start;

    &::before {
        content: '';
        display: block;
        background-color: var(--color-black);
        ${({ bgcolor, palettes }) => css(getBackgroundStyle({ value: bgcolor, palettes }) as CSSObject)};
        width: 24px;
        height: 1px;
    }
`

const FIRST_LOAD_PAGE_SIZE = 3
const DEFAULT_LOAD_PAGE_SIZE = 10

interface Props extends React.ComponentPropsWithoutRef<'div'> {
    node: FlowLayoutNode
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onLoadReplayData: (payload: { pageNum: number; pageSize: number; rootId: string }) => Promise<void>

    idFieldId: string

    title: string
    index: number
    record: RecordLikeProtocol
    total: number
    childRecords?: RecordLikeProtocol[]
    readonly?: boolean

    gap: number

    childIndent?: number

    replayTextColor?: BackgroundNoImageProtocol
    replayIconColor?: BackgroundNoImageProtocol
}

export const ReplayItem = memo(
    ({
        node,
        onRecordClick,
        onLoadReplayData,

        idFieldId,
        title,
        index,
        record,
        total,
        childRecords,
        readonly,
        gap,
        childIndent = 24,
        replayIconColor,
        replayTextColor,
        ...rest
    }: Props) => {
        const { blockData } = useViewBlockContext()
        const { palettes } = useFillPickerContext()

        const isFirstLoadRef = useRef(true)
        const [paging, setPaging] = useImmer({ pageNum: 0, pageSize: DEFAULT_LOAD_PAGE_SIZE, loading: false })

        const restTotal = isFirstLoadRef.current
            ? total
            : Math.min(
                  DEFAULT_LOAD_PAGE_SIZE,
                  total - (paging.pageNum === 0 ? FIRST_LOAD_PAGE_SIZE : paging.pageNum * DEFAULT_LOAD_PAGE_SIZE)
              )

        // 第一次加载pageNum=1&pageSize=3，第二次加载pageNum=1&pageSize=10（重新获取第一页完整数据），后续加载pageNum=2/3/4...&pageSize=10
        const handleLoadMore = useCallback(async () => {
            if (restTotal <= 0) {
                return
            }

            const isFirst = isFirstLoadRef.current
            isFirstLoadRef.current = false

            setPaging(draft => {
                draft.loading = true
            })

            await onLoadReplayData({
                pageNum: paging.pageNum + 1,
                pageSize: isFirst ? FIRST_LOAD_PAGE_SIZE : DEFAULT_LOAD_PAGE_SIZE,
                rootId: record.content[idFieldId]?.value?.toString() || ''
            })

            setPaging(draft => {
                draft.loading = false
                if (isFirst) {
                    return
                }
                draft.pageNum++
            })
        }, [idFieldId, onLoadReplayData, paging.pageNum, record.content, restTotal, setPaging])

        const minHeightConfigure =
            node.children && node.children.length > 0
                ? {}
                : {
                      minHeight: {
                          size: 80,
                          unit: 'px'
                      } as SizeConfigure
                  }

        return (
            <ItemWrapper style={{ gap }} data-record-id={record.id} {...rest}>
                <Item
                    style={{ flex: 'auto' }}
                    recordId={record.id}
                    recordIndexPath={String(index)}
                    readonly={readonly}
                    data={{
                        id: `${blockData.id}/${record.id}`,
                        scope: node.scope,
                        type: 'container',
                        disabled: readonly || index !== 0,
                        virtual: true,
                        data: node.data && {
                            design: node.data.design,
                            size: {
                                width: {
                                    size: 'fill'
                                },
                                height: {
                                    size: 'auto'
                                },
                                ...minHeightConfigure
                            },
                            layout: node.data.layout,
                            position: node.data.position
                        },
                        children: readonly ? node.children : setDeadCopyNode(node.children || [], index === 0, blockData.id)
                    }}
                    title={title}
                    onRecordClick={onRecordClick}
                />

                <NestList style={{ paddingLeft: childIndent, gap }}>
                    {childRecords?.map((child, childIndex) => (
                        <Item
                            key={child.id}
                            recordId={child.id}
                            recordIndexPath={`${index}_${childIndex}`}
                            readonly={readonly}
                            data={{
                                id: `${blockData.id}/${child.id}`,
                                scope: node.scope,
                                type: 'container',
                                disabled: true,
                                virtual: true,
                                data: node.data && {
                                    design: node.data.design,
                                    size: {
                                        width: {
                                            size: 'fill'
                                        },
                                        height: {
                                            size: 'auto'
                                        },
                                        ...minHeightConfigure
                                    },
                                    layout: node.data.layout,
                                    position: node.data.position
                                },
                                children: readonly ? node.children : setDeadCopyNode(node.children || [], false, blockData.id)
                            }}
                            title={title}
                            onRecordClick={onRecordClick}
                        />
                    ))}

                    {restTotal > 0 && (
                        <ExpandButton
                            type="text"
                            onClick={handleLoadMore}
                            loading={paging.loading}
                            bgcolor={replayTextColor}
                            palettes={palettes}
                        >
                            <Text size={14} style={{ ...getTextColorStyle(replayTextColor, palettes) }}>
                                {isFirstLoadRef.current ? `展开 ${restTotal} 条回复` : '展开更多'}
                            </Text>
                            <IconFont
                                type="ArrowDownSmall"
                                color={applyFillPickerValue4CssColorValue(replayIconColor?.color, palettes)}
                                style={{ marginTop: '0.25em' }}
                            />
                        </ExpandButton>
                    )}
                </NestList>
            </ItemWrapper>
        )
    }
)
